/**
 * TransactionsTabContent Component
 *
 * Displays account transaction history using AG Grid with lazy loading,
 * currency formatting, and empty state handling. Self-contained component
 * that owns its own transaction list and loading state — does NOT participate
 * in AccountsContext dirty tracking (transactions save immediately).
 *
 * Table with 6 columns (Date/Time, Type, Description, Amount, Balance After, Actions).
 * Supports add/edit via TransactionModal and delete via inline confirmation modal.
 * Actions column only rendered for manual transactions (not trade-linked).
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link2, MoreVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import type { ColDef } from 'ag-grid-community';

// 2. Workspace packages
import {
  AGGridTable,
  Spinner,
  Autocomplete,
  AutocompleteItem,
  Button,
  ButtonGroup,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from '@enfomentis/ui';
import type { ButtonGroupOption } from '@enfomentis/ui';

// 3. Internal absolute imports
import { TRANSACTION_TYPE_DISPLAY_MAP } from '@/constants';
import { TIMEZONES } from '@/constants/timezone';
import { balanceHistoryService } from '@/services/BalanceHistoryService';
import { basicSettingsService } from '@/services/BasicSettingsService';
import { brokerService } from '@/services/BrokerService';
import type { BalanceHistory } from '@/types/schema/BalanceHistory';
import type { Broker } from '@/types/schema/Broker';
import { fromUTC } from '@/utils/dateHelpers';
import { formatMoney } from '@/utils/moneyHelpers';

// 4. Relative imports
import { useAccounts } from '../context';
import { TransactionModal } from './TransactionModal';

// ---------------------------------------------------------------------------
// Cell Renderers
// ---------------------------------------------------------------------------

/**
 * Renders the transaction type display label with a trade-linked indicator
 * for rows that originated from trade execution.
 */
const TypeRenderer: React.FC<{ data: BalanceHistory }> = ({ data }) => {
  const label = TRANSACTION_TYPE_DISPLAY_MAP[data.transactionType];
  const isTradeLinked = !!(data.sourceExecutionEventId || data.tradeExecutionId);

  return (
    <div className="flex items-center gap-1.5">
      <span>{label}</span>
      {isTradeLinked && (
        <Link2 size={14} className="text-text-tertiary shrink-0" />
      )}
    </div>
  );
};

/**
 * Renders edit/delete actions for manual transactions.
 * Returns null for trade-linked rows (no actions available).
 */
const ActionsRenderer: React.FC<{
  data: BalanceHistory;
  onEditClick: (tx: BalanceHistory) => void;
  onDeleteClick: (tx: BalanceHistory) => void;
}> = ({ data, onEditClick, onDeleteClick }) => {
  const isTradeLinked = !!(data.sourceExecutionEventId || data.tradeExecutionId);
  if (isTradeLinked) return null;

  return (
    <div className="flex items-center justify-center h-full">
      <Dropdown>
        <DropdownTrigger>
          <Button
            size="sm"
            variant="light"
            isIconOnly
            aria-label="Transaction actions"
          >
            <MoreVertical size={16} />
          </Button>
        </DropdownTrigger>
        <DropdownMenu aria-label="Transaction actions">
          <DropdownItem
            key="edit"
            startContent={<Pencil size={16} />}
            onPress={() => onEditClick(data)}
          >
            Edit
          </DropdownItem>
          <DropdownItem
            key="delete"
            color="danger"
            startContent={<Trash2 size={16} />}
            onPress={() => onDeleteClick(data)}
          >
            Delete
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Filter Options
// ---------------------------------------------------------------------------

const TRANSACTION_FILTER_OPTIONS: ButtonGroupOption[] = [
  { value: 'all', label: 'All' },
  { value: 'manual', label: 'Manual Only' },
  { value: 'trade-linked', label: 'Trade-Linked Only' },
];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const TransactionsTabContent: React.FC = () => {
  const {
    selectedAccountId,
    formState,
    activeBrokers,
  } = useAccounts();

  // Local state — self-contained, not part of AccountsContext
  const [transactions, setTransactions] = useState<BalanceHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [transactionFilter, setTransactionFilter] = useState<string>('all');
  const [userTimezone, setUserTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Archived broker lookup — fetches broker by ID when not in activeBrokers
  const [linkedBroker, setLinkedBroker] = useState<Broker | null>(null);

  useEffect(() => {
    if (!formState.brokerId) {
      setLinkedBroker(null);
      return;
    }
    const inActive = activeBrokers.some((b) => b.id === formState.brokerId);
    if (inActive) {
      setLinkedBroker(null);
      return;
    }
    let cancelled = false;
    brokerService.get(formState.brokerId).then((broker) => {
      if (!cancelled) {
        setLinkedBroker(broker ?? null);
      }
    }).catch((error) => {
      console.error('Failed to load linked broker for transactions tab:', error);
      if (!cancelled) {
        setLinkedBroker(null);
      }
    });
    return () => { cancelled = true; };
  }, [formState.brokerId, activeBrokers]);

  // ---------------------------------------------------------------------------
  // Currency resolution: account → broker (active or archived) → USD fallback
  // ---------------------------------------------------------------------------
  const effectiveCurrency = useMemo(() => {
    if (formState.baseCurrency) return formState.baseCurrency;
    const activeBrokerMatch = activeBrokers.find(b => b.id === formState.brokerId);
    const broker = activeBrokerMatch ?? linkedBroker;
    return broker?.feeMetadata?.defaultAccountCurrency || 'USD';
  }, [formState.baseCurrency, formState.brokerId, activeBrokers, linkedBroker]);

  // ---------------------------------------------------------------------------
  // Load user timezone on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    void (async () => {
      try {
        const settings = await basicSettingsService.getOrCreateDefaultSettings();
        if (settings.displayTimezone) {
          setUserTimezone(settings.displayTimezone);
        }
      } catch {
        // Keep browser fallback
      }
    })();
  }, []);

  // ---------------------------------------------------------------------------
  // Load transactions when selected account changes
  // ---------------------------------------------------------------------------
  const loadTransactions = useCallback(async () => {
    if (!selectedAccountId) {
      setTransactions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await balanceHistoryService.getByAccountId(selectedAccountId);
      // Service returns ascending — reverse for most-recent-first display
      setTransactions(data.reverse());
    } catch (error) {
      console.error('Failed to load transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    void loadTransactions();
  }, [loadTransactions]);

  // ---------------------------------------------------------------------------
  // Transaction modal state — single source of truth
  // ---------------------------------------------------------------------------
  const [transactionModalMode, setTransactionModalMode] = useState<'add' | 'edit' | null>(null);
  const [editingTransaction, setEditingTransaction] = useState<BalanceHistory | null>(null);

  const handleTransactionModalClose = useCallback((): void => {
    setTransactionModalMode(null);
    setEditingTransaction(null);
  }, []);

  const handleTransactionModalSaved = useCallback(async (): Promise<void> => {
    await loadTransactions();
    setTransactionModalMode(null);
    setEditingTransaction(null);
  }, [loadTransactions]);

  // ---------------------------------------------------------------------------
  // Timezone override — display-only, not persisted to BasicSettings
  // ---------------------------------------------------------------------------
  const handleTimezoneChange = useCallback((key: React.Key | null): void => {
    if (key) {
      setUserTimezone(String(key));
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Transaction filter — ButtonGroup control, ignores null (prevents deselection)
  // ---------------------------------------------------------------------------
  const handleFilterChange = useCallback(
    (value: string | number | (string | number)[] | null): void => {
      if (value != null && typeof value === 'string') {
        setTransactionFilter(value);
      }
    },
    [],
  );

  const filteredTransactions = useMemo(() => {
    if (transactionFilter === 'all') return transactions;
    if (transactionFilter === 'manual') {
      return transactions.filter(
        (tx) => !tx.sourceExecutionEventId && !tx.tradeExecutionId,
      );
    }
    // 'trade-linked'
    return transactions.filter(
      (tx) => !!(tx.sourceExecutionEventId || tx.tradeExecutionId),
    );
  }, [transactions, transactionFilter]);

  const handleEditClick = useCallback((tx: BalanceHistory): void => {
    setEditingTransaction(tx);
    setTransactionModalMode('edit');
  }, []);

  // ---------------------------------------------------------------------------
  // Delete confirmation state
  // ---------------------------------------------------------------------------
  const [deletingTransaction, setDeletingTransaction] = useState<BalanceHistory | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteClick = useCallback((tx: BalanceHistory): void => {
    setDeletingTransaction(tx);
    setDeleteError(null);
  }, []);

  const handleDeleteCancel = useCallback((): void => {
    setDeletingTransaction(null);
    setDeleteError(null);
  }, []);

  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!deletingTransaction) return;

    setIsDeleting(true);
    try {
      await balanceHistoryService.softDelete(deletingTransaction.id);
      await loadTransactions();
      setDeletingTransaction(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete transaction';
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [deletingTransaction, loadTransactions]);

  // ---------------------------------------------------------------------------
  // Column definitions (6 columns including Actions)
  // ---------------------------------------------------------------------------
  const columnDefs = useMemo<ColDef<BalanceHistory>[]>(() => [
    {
      headerName: 'Date/Time',
      field: 'timestamp',
      flex: 1.5,
      minWidth: 170,
      valueFormatter: (params) =>
        params.value ? fromUTC(params.value, userTimezone, 'display') : '—',
    },
    {
      headerName: 'Type',
      field: 'transactionType',
      width: 140,
      cellRenderer: (params: { data?: BalanceHistory }) =>
        params.data ? <TypeRenderer data={params.data} /> : null,
    },
    {
      headerName: 'Description',
      field: 'description',
      flex: 2,
      minWidth: 150,
      valueFormatter: (params) => params.value || '—',
    },
    {
      headerName: 'Amount',
      field: 'amount',
      width: 140,
      type: 'numericColumn',
      valueFormatter: (params) => {
        if (params.value == null || !params.data) return '—';
        return formatMoney(params.value, effectiveCurrency, params.data.amountPrecision);
      },
      cellClassRules: {
        'text-success': (params) => params.value > 0,
        'text-error': (params) => params.value < 0,
      },
    },
    {
      headerName: 'Balance After',
      field: 'balanceAfter',
      width: 150,
      type: 'numericColumn',
      valueFormatter: (params) => {
        if (params.value == null || !params.data) return '—';
        return formatMoney(params.value, effectiveCurrency, params.data.balancePrecision);
      },
    },
    {
      headerName: '',
      width: 60,
      sortable: false,
      resizable: false,
      cellRenderer: (params: { data?: BalanceHistory }) =>
        params.data ? (
          <ActionsRenderer
            data={params.data}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
          />
        ) : null,
    },
  ], [effectiveCurrency, userTimezone, handleEditClick, handleDeleteClick]);

  // ---------------------------------------------------------------------------
  // Grid options
  // ---------------------------------------------------------------------------
  const gridOptions = useMemo(() => ({
    defaultColDef: {
      resizable: true,
      sortable: true,
    },
    pagination: true,
    paginationPageSize: 25,
    paginationPageSizeSelector: [25, 50, 100] as number[],
    animateRows: true,
  }), []);

  // ---------------------------------------------------------------------------
  // Current balance from most recent transaction
  // ---------------------------------------------------------------------------
  const latestTransaction = transactions[0] as BalanceHistory | undefined;
  const currentBalance = latestTransaction
    ? formatMoney(
        latestTransaction.balanceAfter,
        effectiveCurrency,
        latestTransaction.balancePrecision
      )
    : null;

  // ---------------------------------------------------------------------------
  // Add Transaction button
  // ---------------------------------------------------------------------------
  const addButton = (
    <Button
      size="sm"
      color="primary"
      variant="flat"
      startContent={<Plus size={16} />}
      onPress={() => {
        setEditingTransaction(null);
        setTransactionModalMode('add');
      }}
      className='shrink-0'
      isDisabled={!selectedAccountId}
    >
      Add Transaction
    </Button>
  );

  // ---------------------------------------------------------------------------
  // Render — single tree with computed content + shared modals
  // ---------------------------------------------------------------------------

  let content: React.ReactNode;

  if (loading) {
    content = (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" label="Loading transactions..." />
      </div>
    );
  } else if (transactions.length === 0) {
    content = (
      <div className="flex items-center justify-center py-12 text-center">
        <div>
          <p className="text-text-secondary mb-2">
            No transactions found for this account
          </p>
          <p className="text-sm text-text-tertiary mb-4">
            Add a transaction to start tracking your account balance
          </p>
          {addButton}
        </div>
      </div>
    );
  } else {
    content = (
      <>
        {/* Balance header */}
        <div className="px-1">
          <span className="text-sm text-text-tertiary">Current Balance</span>
          <p className="text-lg font-semibold">{currentBalance}</p>
        </div>

        {/* Actions row: Filter (left), Timezone + Add Transaction (right) */}
        <div className="flex items-end justify-between gap-3 px-1">
          <div className="flex items-center gap-3">
            <span className="text-sm text-text-primary">Filter:</span>
            <ButtonGroup
              options={TRANSACTION_FILTER_OPTIONS}
              value={transactionFilter}
              onChange={handleFilterChange}
              variant="light"
              radius="none"
              className="shrink-0"
              selectedColor="primary"
              unselectedColor="default"
            />
          </div>
          <div className="flex items-center gap-3">
            <Autocomplete
              label="Timezone"
              labelPlacement="outside-left"
              placeholder="Search timezones..."
              size="sm"
              variant="underlined"
              selectedKey={userTimezone}
              menuTrigger="input"
              onSelectionChange={handleTimezoneChange}
              aria-label="Display timezone"
            >
              {TIMEZONES.map((tz) => (
                <AutocompleteItem key={tz.value}>
                  {tz.label}
                </AutocompleteItem>
              ))}
            </Autocomplete>
            {addButton}
          </div>
        </div>

        {/* Transactions table or filtered-empty state */}
        {filteredTransactions.length > 0 ? (
          <div className="flex-1 min-h-0">
            <AGGridTable<BalanceHistory>
              rowData={filteredTransactions}
              columnDefs={columnDefs}
              height="100%"
              gridOptions={gridOptions}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center py-12 text-center">
            <p className="text-text-secondary">
              No {transactionFilter === 'manual' ? 'manual' : 'trade-linked'} transactions found
            </p>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      {content}

      {/* Transaction add/edit modal */}
      {selectedAccountId && (
        <TransactionModal
          isOpen={transactionModalMode !== null}
          onClose={handleTransactionModalClose}
          onSaved={handleTransactionModalSaved}
          accountId={selectedAccountId}
          effectiveCurrency={effectiveCurrency}
          userTimezone={userTimezone}
          {...(editingTransaction ? { editTransaction: editingTransaction } : {})}
        />
      )}

      {/* Delete confirmation modal */}
      <Modal
        isOpen={!!deletingTransaction}
        onClose={handleDeleteCancel}
        size="sm"
        placement="top-center"
        isDismissable={!isDeleting}
      >
        <ModalContent>
          <ModalHeader>Delete Transaction?</ModalHeader>
          <ModalBody>
            {deletingTransaction && (
              <p className="text-text-secondary">
                Delete this{' '}
                {TRANSACTION_TYPE_DISPLAY_MAP[deletingTransaction.transactionType]}{' '}
                transaction for{' '}
                {formatMoney(
                  deletingTransaction.amount,
                  effectiveCurrency,
                  deletingTransaction.amountPrecision
                )}
                ? This action cannot be undone.
              </p>
            )}
            {deleteError && (
              <Alert color="danger" variant="flat">
                {deleteError}
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="flat"
              onPress={handleDeleteCancel}
              isDisabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={handleDeleteConfirm}
              isLoading={isDeleting}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
